import {app, fs, fse, isDevMode} from '../../electron-modules';
import path from "path";
import {pathWithExtension} from "../../utilities";
import LocalScene from "../../viewer/js/projections";
import slash from "slash";
import {Asset, AssetType, CameraData} from "../../redux/assets-slice";
import {t} from "../../localization";
import { PythonExecutable } from '../../executable';

export const deleteFolder = async (folderPath: string) => {
  try {
    await fse.remove(folderPath);
  } catch {}
};

export const createNewFolder = async (folderPath: string) => {
  await deleteFolder(folderPath);
  await fse.mkdirs(folderPath);
};

export const moveCameraData = async (asset: Asset, dataPath: string) => {
  const imagesPath = path.join(dataPath, asset.id);

  await createNewFolder(imagesPath);

  const imagesData = asset.data as CameraData[];
  for (let i= 0; i < imagesData.length; i++) {
    const imageData = imagesData[i];
    const imageName = path.basename(imageData.path);
    const newImagePath = path.join(imagesPath, imageName);

    if (fs.existsSync(imageData.path)) {
      await fse.copy(imageData.path, newImagePath);
    }
  }
};

/** Execute python function to copy folders instead. Nodejs is
 * very slow and has issues with large files */
export const moveStreamablePointsData = async (asset: Asset,
  dataPath: string, exe: PythonExecutable) => {
  const inputPath = asset.path;
  const outputPath = path.join(dataPath, asset.id);

  return new Promise((resolve) => {
    let commands = [
      "-p", "copy_folder",
      "--input_folder", inputPath,
      "--output_folder", outputPath
    ];

    exe.run({
      commands,
      onClose: async () => {
        resolve(true);
      }
    });
  });
};

export const moveGenericPointCloud = async (asset: Asset, dataPath: string,
  exe: PythonExecutable, onLogs, singleFileOutput: boolean) => {
  const inputPath = asset.path;
  const pointPath = path.join(dataPath, asset.id);

  return new Promise((resolve) => {
    let commands = [
      "-p", "process_las",
      "--input_path", inputPath,
      "--output_folder", pointPath,
      "--single_file", singleFileOutput,
      "--units", LocalScene.dataProjectionUnits
    ];

    exe.run({
      commands,
      saveLogs: true,
      onLogs: onLogs,
      onClose: async () => {
        resolve(true);
      }
    });
  });
};

export const getBuildPath = () => {
  const appPath = slash(app.getAppPath());

  return isDevMode
    ? path.join(appPath, "build")
    : path.join(path.dirname(appPath), "app.asar.unpacked", "build");
};

export const getMiniViewerPath = () => {
  const appPath = slash(app.getAppPath());
  return isDevMode
    ? path.join(appPath, "miniviewer.exe")
    : path.join(path.dirname(appPath), "app.asar.unpacked", "miniviewer.exe");
};

export const moveModelData = async (asset: Asset, dataPath: string, onExportError) => {
  const outputFolder = path.join(dataPath, asset.id);
  const modelName = path.basename(asset.path);
  const copyToFile = async (asset, inputPath, outputPath) => {
    try {
      await fse.copy(inputPath, outputPath);
    } catch {
      onExportError(
        t("dialog.general-error-title"),
        t("dialog.export-error-generic", {name: asset.name})
      );
      return;
    }
  };

  await createNewFolder(outputFolder);

  if (asset.type === AssetType.SHP) {
    const shpInput = pathWithExtension(asset.path, "shp");
    const shpOutput = path.join(outputFolder, pathWithExtension(modelName, "shp"));
    await copyToFile(asset, shpInput, shpOutput);

    const dbfInput = pathWithExtension(asset.path, "dbf");
    const dbfOutput = path.join(outputFolder, pathWithExtension(modelName, "dbf"));
    await copyToFile(asset, dbfInput, dbfOutput);
  } else {
    const inputPath = asset.path;
    const outputPath = path.join(outputFolder, modelName);
    await copyToFile(asset, inputPath, outputPath);
  }
};